/**
 * ChatContextBar - Displays context documents as removable chips
 * Shows active document (auto-added) and manually added references
 */

import { useAI, ContextDocument } from '../../contexts/AIContext';
import { Button } from '@/components/ui';

interface ChatContextBarProps {
  onAddContext?: () => void; // Callback to trigger @context command
}

export default function ChatContextBar({ onAddContext }: ChatContextBarProps) {
  const { contextDocuments, removeContextDocument } = useAI();

  if (contextDocuments.length === 0 && !onAddContext) {
    return null;
  }

  // Get icon based on document type
  const getTypeIcon = (type: ContextDocument['type']) => {
    switch (type) {
      case 'active-tab':
        return '📄';
      case 'document':
        return '📝';
      case 'reference':
        return '📚';
      case 'project':
        return '📁';
      default:
        return '📄';
    }
  };

  // Get chip styling based on type
  const getChipStyles = (doc: ContextDocument) => {
    const baseStyles = 'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-sm font-medium transition-colors';
    
    if (doc.isAutoAdded) {
      // Auto-added (active tab) - subtle blue styling
      return `${baseStyles} bg-muted text-muted-foreground border border-border dark:border-info/40`;
    }
    
    switch (doc.type) {
      case 'reference':
        // Reference documents - purple
        return `${baseStyles} bg-secondary text-secondary-foreground border border-border`;
      case 'project':
        // Project documents - green
        return `${baseStyles} bg-success-light text-success border border-success`;
      default:
        // Default - gray
        return `${baseStyles} bg-muted text-foreground border border-border`;
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Context label */}
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        Context:
      </span>

      {/* Context chips */}
      {contextDocuments.map((doc) => (
        <div
          key={doc.id}
          className={getChipStyles(doc)}
          title={doc.isAutoAdded ? `Active: ${doc.title}` : doc.title}
        >
          <span className="text-sm">{getTypeIcon(doc.type)}</span>
          <span className="max-w-[150px] truncate">{doc.title}</span>
          <Button
            onClick={() => removeContextDocument(doc.id)}
            variant="ghost"
            size="icon"
            className="ml-0.5 h-4 w-4 hover:bg-black/10 dark:hover:bg-white/10"
            title="Remove from context"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </Button>
        </div>
      ))}

      {/* Add context button */}
      {onAddContext && (
        <Button
          onClick={onAddContext}
          variant="ghost"
          size="xs"
          className="text-muted-foreground hover:text-foreground"
          title="Add document to context (Cmd+K)"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add
        </Button>
      )}

      {/* Empty state hint */}
      {contextDocuments.length === 0 && !onAddContext && (
        <span className="text-xs text-muted-foreground italic">
          No documents in context
        </span>
      )}
    </div>
  );
}

